import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiMutate } from '../composables/apiMutate'

export const MAX_FILE_BYTES = 10 * 1024 * 1024
export const ALLOWED_MIMES = ['application/pdf', 'text/markdown', 'text/plain'] as const

const EXT_TO_MIME: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.md': 'text/markdown',
  '.txt': 'text/plain',
}

export class SourceValidationError extends Error {}

export interface FileSubmissionResult {
  file: File
  ok: boolean
  /** Validation message or the backend's 4xx `error` field. Only set when `ok === false`. */
  error?: string
}

export interface FileRejection {
  filename: string
  reason: string
}

/**
 * Split a batch of dropped files into the ones that pass client-side
 * validation and the ones that don't. Used by the Add-to-wiki drawer to
 * populate its `pickedFiles` + `fileRejections` refs in one pass. Pure
 * so it lives with the validation rules (and its test lives with theirs)
 * rather than inside the drawer component where mounting the full Vue
 * tree would be disproportionate.
 */
export function partitionFiles(files: File[]): {
  accepted: File[]
  rejected: FileRejection[]
} {
  const accepted: File[] = []
  const rejected: FileRejection[] = []
  for (const file of files) {
    try {
      validateFile(file)
      accepted.push(file)
    } catch (e) {
      rejected.push({
        filename: file.name,
        reason: e instanceof Error ? e.message : 'invalid file',
      })
    }
  }
  return { accepted, rejected }
}

// Stable identity for a picked File — name + size + lastModified —
// independent of the File object reference. Browsers don't expose a file
// identity, but this triple is unique enough to distinguish the files in
// one picker batch.
export function fileIdentityKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

// Given the files currently in the picker and the per-file submit
// results, return the files to KEEP for retry — those NOT accepted.
// Matches on `fileIdentityKey`, NOT File-reference identity, so the prune
// survives `submitFiles` cloning or wrapping the File on its way through
// (e.g. attaching upload metadata) — a future change there would
// otherwise silently leave every accepted file stuck in the picker.
// Files added to the picker after the batch was submitted (not
// present in `results`) are retained.
export function filesToRetry(picked: File[], results: FileSubmissionResult[]): File[] {
  const acceptedKeys = new Set(results.filter((r) => r.ok).map((r) => fileIdentityKey(r.file)))
  return picked.filter((f) => !acceptedKeys.has(fileIdentityKey(f)))
}

/**
 * Submit helpers for every Source kind the drawer can create — Ask
 * (web_prompt), File (file_upload), URL (url). The created Source flows
 * into the shared source_activity list via the SourceChannel broadcast,
 * so this store holds only submit-time loading/error state.
 *
 * One store keeps the submit boilerplate (POST, 4xx handling, error
 * capture) in a single place and gives the drawer one mutation surface
 * for the whole "add something to the wiki" workflow.
 */
export const useSourcesStore = defineStore('sources', () => {
  const submitting = ref(false)
  const submitError = ref<string | null>(null)

  /**
   * Submit a web_prompt with an optional caller-supplied category
   * When `category` is set the controller maps it to a
   * `metadata.template_dir` value the SourceDispatcher reads at
   * job time to pin the LLM's write target (or route to the memory
   * extraction service). Unset / empty / unknown values fall through
   * to the existing LLM auto-classify path on the server.
   */
  async function submitAsk(content: string, category?: string): Promise<void> {
    const payload: Record<string, unknown> = { kind: 'web_prompt', content }
    if (category && category.trim() !== '') payload.category = category
    await runSubmit(() => postSource(payload))
  }

  async function submitFile(file: File): Promise<void> {
    await runSubmit(async () => {
      validateFile(file)
      await postFile(file)
    })
  }

  /**
   * Submit N files as independent Sources. Each file goes through its
   * own validate → base64 → POST pipeline so a single failure doesn't
   * short-circuit the rest of the batch. Returns one result per input
   * file so the caller can render a per-file summary; `submitError` is
   * populated with a terse roll-up if any file failed.
   *
   * `throwOnError: false` is passed to `runSubmit` so a partial-batch
   * failure doesn't raise — the caller consumes per-file results
   * instead. The single-file `submitFile` keeps the default throwing
   * behaviour so its existing contract is unchanged.
   */
  async function submitFiles(files: File[]): Promise<FileSubmissionResult[]> {
    if (files.length === 0) return []
    return runSubmit(
      async () => {
        const results = await Promise.all(files.map(attemptSubmit))
        const failed = results.filter((r) => !r.ok)
        if (failed.length > 0) {
          submitError.value =
            failed.length === files.length
              ? `All ${files.length} uploads failed.`
              : `${failed.length} of ${files.length} uploads failed.`
        }
        return results
      },
      { throwOnError: false },
    )
  }

  async function attemptSubmit(file: File): Promise<FileSubmissionResult> {
    try {
      validateFile(file)
      await postFile(file)
      return { file, ok: true }
    } catch (e) {
      return { file, ok: false, error: e instanceof Error ? e.message : 'submit failed' }
    }
  }

  async function postFile(file: File): Promise<void> {
    const base64 = await readAsBase64(file)
    await postSource({
      kind: 'file_upload',
      content_base64: base64,
      filename: file.name,
      mime_type: inferredMime(file),
    })
  }

  async function submitUrl(url: string): Promise<void> {
    await runSubmit(() => {
      validateUrl(url)
      return postSource({ kind: 'url', url: url.trim() })
    })
  }

  /**
   * Cancel an in-flight Source. Treats 404 as success — it means the
   * source has already reached a terminal state (another admin cancelled,
   * or the job just finished), and the row is about to be replaced by a
   * terminal broadcast regardless. Other non-2xx responses throw so the
   * caller can surface the error.
   */
  async function cancel(id: number): Promise<void> {
    await apiMutate(`/api/v1/sources/${id}`, {
      method: 'DELETE',
      failureLabel: 'cancel failed',
      tolerateStatuses: [404],
    })
  }

  /**
   * Wraps a submit action with the shared `submitting` + `submitError`
   * lifecycle. By default re-throws so a single submit's caller sees
   * the error; batch submitters set `throwOnError: false` when they've
   * already captured per-item outcomes in the return value and don't
   * want a roll-up to propagate as an exception.
   */
  async function runSubmit<T>(fn: () => Promise<T>, options: { throwOnError?: boolean } = {}): Promise<T> {
    const { throwOnError = true } = options
    submitting.value = true
    submitError.value = null
    try {
      return await fn()
    } catch (e) {
      submitError.value = e instanceof Error ? e.message : 'submit failed'
      if (throwOnError) throw e
      // Unreachable in practice — the batch path catches per-file errors
      // and never throws. Returning the caller's expected type keeps the
      // generic signature honest for future non-throwing wrappers.
      return undefined as T
    } finally {
      submitting.value = false
    }
  }

  return { submitting, submitError, submitAsk, submitFile, submitFiles, submitUrl, cancel }
})

export function inferredMime(file: File): string {
  if (file.type && (ALLOWED_MIMES as readonly string[]).includes(file.type)) return file.type
  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? ''
  return EXT_TO_MIME[ext] ?? file.type ?? ''
}

export function validateFile(file: File): void {
  if (file.size === 0) throw new SourceValidationError('file is empty')
  if (file.size > MAX_FILE_BYTES) {
    throw new SourceValidationError(`file exceeds ${MAX_FILE_BYTES / 1_048_576} MB limit`)
  }
  const mime = inferredMime(file)
  if (!(ALLOWED_MIMES as readonly string[]).includes(mime)) {
    throw new SourceValidationError(`unsupported file type: ${mime || '(unknown)'}`)
  }
}

export function validateUrl(url: string): void {
  const trimmed = url.trim()
  if (!trimmed) throw new SourceValidationError('url required')
  if (!/^https?:\/\//i.test(trimmed)) {
    throw new SourceValidationError('url must start with http:// or https://')
  }
}

async function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('file reader returned non-string'))
        return
      }
      // Strip the "data:<mime>;base64," prefix so the body matches the
      // strict_decode64 expectation on the backend.
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    })
    reader.addEventListener('error', () => reject(reader.error ?? new Error('file read failed')))
    reader.readAsDataURL(file)
  })
}

async function postSource(source: Record<string, unknown>): Promise<void> {
  await apiMutate('/api/v1/sources', {
    method: 'POST',
    body: { source },
    failureLabel: 'submit failed',
  })
}
